import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { i18n } from 'i18n';
import { FileParameter, ProjectDto, useUpdateTunnelProfile } from 'providers/api';
import { Model } from '../../hooks/useModelState';
import { buildTunnelProfileFromDto } from '../../MineModel/TunnelProfile/TunnelProfile';

export const TUNNEL_PROFILE_FILE_ACCEPT = '.obj,.dxf,.duf';

interface UseTunnelProfileImportProps {
  model: Model;
  project: ProjectDto;
}

const useTunnelProfileImport = ({ model, project }: UseTunnelProfileImportProps) => {
  const [isTunnelProfileImporting, setIsTunnelProfileImporting] = useState(false);
  const updateTunnelProfileMutation = useUpdateTunnelProfile(project.entityId as string);

  // Upload the whole file to the backend (server converts a DUF's full contents — all solids and
  // strings — to OBJ), then rebuild the in-memory profile from the persisted DTO so it renders and
  // survives refresh.
  const importTunnelProfile = async (file: File) => {
    setIsTunnelProfileImporting(true);
    try {
      const fileParam: FileParameter = { data: file, fileName: file.name };
      const dto = await updateTunnelProfileMutation.mutateAsync({ file: fileParam });
      const profile = await buildTunnelProfileFromDto(dto);
      if (!profile) {
        enqueueSnackbar(i18n._('No supported tunnel profile geometry was found in this file.'), { variant: 'error' });
        return;
      }
      model.setTunnelProfile(profile);
      enqueueSnackbar(i18n._('Tunnel profile imported'), { variant: 'success' });
    } catch {
      enqueueSnackbar(i18n._('Failed to import tunnel profile'), { variant: 'error' });
    } finally {
      setIsTunnelProfileImporting(false);
    }
  };

  return { isTunnelProfileImporting, importTunnelProfile };
};

export default useTunnelProfileImport;
